import { useEffect } from 'react';
import {
  ServiceRequestFormContextValue,
  useServiceRequestFormContext,
} from 'src/components/ServiceRequestForm/CustomHooks/ServiceRequestFormContext';
import { ServiceRequestFormStep } from 'src/components/ServiceRequestForm/CustomHooks/serviceRequestFormSteps';

export interface IssueTypeOption {
  id: string;
  name: string;
  title: string;
}

export interface UseServiceRequestFormIssueTypeOrchestratorReturn {
  diagnosisAsset: string;
  diagnosisIssueType: string;
  warrantyExpiresAt: string;
  visibleIssueTypes: IssueTypeOption[];
  selectedIssueTypeName: string;
  showIssueTypePrompt: boolean;
  nextButtonLabel: string;
  canProceed: boolean;
  onSelectIssueType: (option: IssueTypeOption) => void;
  onNext: () => void;
  onBack: () => void;
  onExit: () => void;
}

const toIssueTypeOption = (type: any): IssueTypeOption => {
  return {
    id: type.id,
    name: type.name,
    title: type.name === 'Heating/Cooling' ? 'Heating / Cooling' : type.name,
  };
};

export const useServiceRequestFormIssueTypeOrchestrator =
  (): UseServiceRequestFormIssueTypeOrchestratorReturn => {
    const {
      formData,
      patchFormData,
      goNext,
      goPrev,
      goToStep,
      user,
      onExit,
      reference,
    }: ServiceRequestFormContextValue = useServiceRequestFormContext();

    const { issueTypes, faqs, getFaqsForIssueType } = reference;

    useEffect(() => {
      if (
        !formData.issueType &&
        !formData.issueTypeId &&
        issueTypes.length > 0
      ) {
        const general: any = issueTypes.find(
          (type: any) => type.name === 'General'
        );

        if (general) {
          patchFormData({ issueType: general.name, issueTypeId: general.id });
        }
      }
    }, []);

    const isHomeOwner: boolean = user?.roles?.[0]?.role?.name === 'Home Owner';

    const generalIssueType: any = issueTypes.find(
      (type: any) => type.name === 'General'
    );

    const homeOwnerIssueTypeOptions: IssueTypeOption[] = generalIssueType
      ? [toIssueTypeOption(generalIssueType)]
      : [];

    const allIssueTypeOptions: IssueTypeOption[] = issueTypes.map((type: any) =>
      toIssueTypeOption(type)
    );

    const visibleIssueTypes: IssueTypeOption[] = isHomeOwner
      ? homeOwnerIssueTypeOptions
      : allIssueTypeOptions;

    const hasSelectedIssueType: boolean = Boolean(formData.issueType);

    const faqsForSelectedIssueType: any[] = getFaqsForIssueType(
      formData.issueType
    );

    const hasRelevantFaqs: boolean = isHomeOwner
      ? faqs.length > 0
      : faqsForSelectedIssueType.length > 0;

    const onSelectIssueType = (option: IssueTypeOption): void => {
      patchFormData({ issueType: option.name, issueTypeId: option.id });
    };

    const onNext = (): void => {
      if (hasRelevantFaqs) {
        goNext();
        return;
      }

      goToStep(ServiceRequestFormStep.Details);
    };

    const onBack = (): void => {
      goPrev();
    };

    const showIssueTypePrompt: boolean = !isHomeOwner && !hasSelectedIssueType;

    const nextButtonLabel: string = hasRelevantFaqs
      ? 'Next: Common Questions'
      : 'NEXT: ADD DETAILS';

    return {
      diagnosisAsset: formData.asset_item_nameDescription,
      diagnosisIssueType: formData.issueType,
      warrantyExpiresAt: formData.assetWarrantyExpiresAt,
      visibleIssueTypes,
      selectedIssueTypeName: formData.issueType,
      showIssueTypePrompt,
      nextButtonLabel,
      canProceed: hasSelectedIssueType,
      onSelectIssueType,
      onNext,
      onBack,
      onExit,
    };
  };